import { Alignment, DOMUtil } from "@mcleod/core";
import { DesignerInterface } from "../../../../base/DesignerInterface";
import { FilterTextboxPanel } from "./FilterTextboxPanel";
import { Panel } from "../../../panel/Panel";
import { PanelProps } from "../../../panel/PanelProps";
import { RowCountLabel } from "./RowCountLabel";
import { SearchTextboxPanel } from "./SearchTextboxPanel";
import { Table } from "../../Table";

export type FilterAlignment = Alignment.BOTTOM | Alignment.RIGHT;

export enum SearchFilterMode {
    EXPANDED = "expanded",
    COLLAPSED = "collapsed"
}

enum InnerIds {
    LABEL_ROW_COUNT = "labelRowCount",
    PANEL_FILTER = "panelFilter",
    PANEL_SEARCH = "panelSearch",
}

export class SearchFilterPanel extends Panel {
    private firstSearchRun = false;
    private _labelRowCount: RowCountLabel;
    private _panelFilter: FilterTextboxPanel;
    private _panelSearch: SearchTextboxPanel;
    private table: Table;

    constructor(table: Table, props?: Partial<PanelProps>) {
        super({ margin: 0, padding: 0, rowBreak: false, wrap: false, _designer: table._designer, ...props });
        this.allowDropInDesigner = false;
        this.table = table;
        this.createComponents();
        this.add(this.panelSearch);
        this.add(this.panelFilter);
        this.syncFields(false);
    }

    private createComponents() {
        this.createSearchTextbox();
        this.createFilterTextbox();
        this.createRowCountLabel();
    }

    private createSearchTextbox() {
        this.panelSearch = new SearchTextboxPanel(this.table,
            { id: this.createInnerComponentId(InnerIds.PANEL_SEARCH) });
        this.panelSearch.onFilterToggle = this.toggleFilterVisible;
    }

    private createFilterTextbox() {
        this.panelFilter = new FilterTextboxPanel(this.table,
            { id: this.createInnerComponentId(InnerIds.PANEL_FILTER) });
    }

    private createRowCountLabel() {
        this.labelRowCount = new RowCountLabel(this.table,
            { id: this.createInnerComponentId(InnerIds.LABEL_ROW_COUNT) });
    }

    override syncInnerComponentIds() {
        this.setInnerComponentId(this.labelRowCount, InnerIds.LABEL_ROW_COUNT);
        this.setInnerComponentId(this.panelFilter, InnerIds.PANEL_FILTER);
        this.setInnerComponentId(this.panelSearch, InnerIds.PANEL_SEARCH);
    }

    get panelSearch(): SearchTextboxPanel {
        return this._panelSearch;
    }

    private set panelSearch(value: SearchTextboxPanel) {
        this._panelSearch = value;
    }

    get panelFilter(): FilterTextboxPanel {
        return this._panelFilter;
    }

    private set panelFilter(value: FilterTextboxPanel) {
        this._panelFilter = value;
    }

    get labelRowCount(): RowCountLabel {
        return this._labelRowCount;
    }
    
    private set labelRowCount(value: RowCountLabel) {
        this._labelRowCount = value;
    }

    refreshTextboxSearchTooltip() {
        this.panelSearch.refreshTooltip();
    }

    refreshRowCountLabel() {
        this.labelRowCount.refresh();
    }

    syncFields(recreateFields: boolean) {
        if (this.panelFilter == null || this.panelSearch == null) {
            return;
        }
        const panelSearchVisible = this.checkPanelSearchVisibility();
        const panelFilterVisible = this.checkPanelFilterVisibility();
        this.panelSearch.syncFields(panelSearchVisible, recreateFields);
        this.panelFilter.syncFields(panelFilterVisible, recreateFields);
        this.syncLabelRowCount(panelSearchVisible || panelFilterVisible);
        this.placeFilterPanel();
    }

    placeFilterPanel() {
        // - If the search filter mode is expanded, and the filter is set to display below the search field, and both
        //   the search & filter fields are available, and this panel, when showing all of its options, would be the
        //   tallest of the heading panel's inner panels...then remove the FilterTextboxPanel from this panel and add it
        //   directly to the table header.  The table header panel will manage its horizontal spacing so that it appears
        //   directly below the search panel.
        // - Otherwise, all components will be rendered in this panel.
        if (this.table.headingPanel != null/* && this.placingFilterPanel !== true*/) {
            const displayFilterInSecondRow = this.shouldDisplayFilterInSecondRow();
            if (displayFilterInSecondRow) {
                this.remove(this.panelFilter);
            }
            this.table.headingPanel.displayFilterBelowSearch(displayFilterInSecondRow);
            if (!displayFilterInSecondRow) {
                this.addIfNotPresent(this.panelFilter);
            }
        }
    }

    private get totalHeightNeeded(): number {
        const thisPanelHeight = DOMUtil.getElementHeight(this._element);
        if (this.contains(this.panelFilter)) {
            return thisPanelHeight;
        }
        const filterPanelHeight = DOMUtil.getElementHeight(this.panelFilter._element);
        return thisPanelHeight + filterPanelHeight;
    }

    private shouldDisplayFilterInSecondRow(): boolean {
        let result =
            this.panelSearch.optionIsAvailable() &&
            this.table.allowFilter &&
            this.table.searchFilterMode === SearchFilterMode.EXPANDED &&
            this.table.filterAlignment === Alignment.BOTTOM;
        if (result === true) {
            const tallestOtherPanel = this.table.headingPanel.getInnerPanelsMaxHeight([ this ]);
            result = this.totalHeightNeeded > tallestOtherPanel;
        }
        return result;
    }

    private syncLabelRowCount(makeVisible: boolean) {
        this.remove(this.labelRowCount);
        const panelSearchVisible = !this.panelSearch.isEmpty();
        if (panelSearchVisible) {
            this.labelRowCount.rowBreak = this.table.filterAlignment === Alignment.BOTTOM;
            this.insert(this.labelRowCount, 1);
        }
        else {
            this.labelRowCount.rowBreak = false;
            this.add(this.labelRowCount);
        }
        this.labelRowCount.visible = makeVisible;
    }

    private checkPanelSearchVisibility(): boolean {
        return this.table.allowDbSearch;
    }

    private checkPanelFilterVisibility(): boolean {
        // Only show the filter when
        // - It's allowed
        // - When the table contains records
        if (this.table.allowFilter !== true)
            return false;
        if (this.table.allowDbSearch === true) {
            // If in expanded mode, show the filter box when the user clicks the funnel image inside the search textbox.
            // If in collapsed mode, show the filter box when the user has run at least one search.
            if (this.table.searchFilterMode === SearchFilterMode.EXPANDED) {
                return this.panelSearch.filterToggledOn;
            }
            else {
                return this.firstSearchRun;
            }
        }
        // The filter is allowed and the table is not empty, so display the filter.
        return true;
    }

    doAfterDbSearch() {
        this.panelSearch.updateAfterDbSearch(this.checkPanelFilterVisibility());
        this.firstSearchRun = true;
        this.syncFields(false);
        this.panelFilter.clearTextbox();
    }

    private toggleFilterVisible = () => {
        this.syncFields(false);
        this.panelSearch.syncFunnelColor(this.checkPanelFilterVisibility());
    }

    focusSearchFilter() {
        if (this.contains(this.panelSearch)) {
            this.panelSearch.focus();
        }
        else if (this.contains(this.panelFilter)) {
            this.panelFilter.focus();
        }
    }

    override get _designer(): DesignerInterface {
        return super._designer;
    }

    override set _designer(value: DesignerInterface) {
        super._designer = value;
        this._labelRowCount._designer = value;
        this._panelFilter._designer = value;
        this._panelSearch._designer = value;
    }
}
